import { app, isConfigured } from './firebase.js';
import { socket, userName as guestUserName, resetToAnonName } from './socket.js';
import { modalOpen, modalClose } from './modal-state.js';
import { DATE_LOCALE } from './date-locale.js';
import { router } from './router.js';

const apiUrl = import.meta.env.VITE_API_BASE_URL;

let auth = null;
let onAuthStateChanged, signInWithPopup, GoogleAuthProvider,
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  updateProfile, firebaseSignOut, sendEmailVerification, sendPasswordResetEmail;

let firebaseAuthLoaded = false;
async function loadFirebaseAuth() {
  if (firebaseAuthLoaded) return;
  firebaseAuthLoaded = true;
  const mod = await import('firebase/auth');
  onAuthStateChanged = mod.onAuthStateChanged;
  signInWithPopup = mod.signInWithPopup;
  GoogleAuthProvider = mod.GoogleAuthProvider;
  signInWithEmailAndPassword = mod.signInWithEmailAndPassword;
  createUserWithEmailAndPassword = mod.createUserWithEmailAndPassword;
  updateProfile = mod.updateProfile;
  firebaseSignOut = mod.signOut;
  sendEmailVerification = mod.sendEmailVerification;
  sendPasswordResetEmail = mod.sendPasswordResetEmail;
  auth = mod.getAuth(app);

  onAuthStateChanged(auth, async (user) => {
    currentUser = user;
    if (user) {
      cachedToken = await user.getIdToken();
      if (!backendUser) {
        await fetchMe();
      }
    } else {
      cachedToken = null;
      backendUser = null;
    }
    notifyListeners();
    updateDisplayNameUI();
    syncSocketUserName();
  });

  // Wait until persisted session is restored — the first onAuthStateChanged
  // callback can fire as null before Firebase finishes reading local storage.
  await auth.authStateReady();
  currentUser = auth.currentUser;
  if (currentUser) {
    cachedToken = await currentUser.getIdToken();
    if (!backendUser) {
      await fetchMe();
    }
  }
  authReadyResolve();
}

if (isConfigured) {
  loadFirebaseAuth();
}

let currentUser = null;
let cachedToken = null;
let backendUser = null;
let authReadyResolve;
const authReadyPromise = new Promise((r) => { authReadyResolve = r; });
const authListeners = new Set();

if (!isConfigured) {
  authReadyResolve();
}

function notifyListeners() {
  for (const fn of authListeners) {
    try { fn(currentUser); } catch (_) { /* noop */ }
  }
}

export function onAuthChange(fn) {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

export function isLoggedIn() {
  return !!currentUser;
}

export function getBackendUser() {
  return backendUser;
}

export function getDisplayName() {
  if (backendUser?.display_name) {
    return backendUser.display_name;
  }
  if (currentUser) {
    return currentUser.displayName || currentUser.email?.split('@')[0] || 'user';
  }
  return guestUserName;
}

export async function getToken() {
  if (!currentUser) return null;
  cachedToken = await currentUser.getIdToken();
  return cachedToken;
}

export function getCachedToken() {
  return cachedToken;
}

export async function waitForAuth() {
  return authReadyPromise;
}

// ── Backend API ──

async function fetchMe() {
  const token = await getToken();
  if (!token) return null;

  try {
    const res = await fetch(`${apiUrl}/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.user) {
      backendUser = data.user;
      updateDisplayNameUI();
      syncSocketUserName();
      return backendUser;
    }
    return null;
  } catch (e) {
    console.warn('Failed to fetch user profile:', e.message);
    return null;
  }
}

async function registerOnBackend() {
  const token = await getToken();
  if (!token) return null;

  try {
    const res = await fetch(`${apiUrl}/auth/register`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (res.status === 409) {
      return await fetchMe();
    }

    const data = await res.json();

    if (res.ok && data.user) {
      backendUser = data.user;
      updateDisplayNameUI();
      syncSocketUserName();
      return backendUser;
    }

    console.warn('Backend registration returned unexpected status:', res.status, data);
    return null;
  } catch (e) {
    console.warn('Backend registration failed:', e.message);
    return null;
  }
}

async function updateBackendDisplayName(newName) {
  const res = await authFetch(`${apiUrl}/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: newName }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    if (res.status === 409) {
      throw new Error('this display name is already taken');
    }
    throw new Error(data.detail || data.error || 'failed to update name');
  }

  const data = await res.json();
  if (data.user) {
    backendUser = data.user;
  } else {
    backendUser = { ...backendUser, display_name: newName };
  }

  updateDisplayNameUI();
  syncSocketUserName();
}

// ── Firebase Auth Actions ──

export async function signInWithGoogle() {
  await loadFirebaseAuth();
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  cachedToken = await result.user.getIdToken();
  await registerOnBackend();
  return result.user;
}

export async function loginWithEmail(email, password) {
  await loadFirebaseAuth();
  const result = await signInWithEmailAndPassword(auth, email, password);
  cachedToken = await result.user.getIdToken();
  await registerOnBackend();
  return result.user;
}

export async function registerWithEmail(email, password) {
  await loadFirebaseAuth();
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(result.user);
  cachedToken = await result.user.getIdToken();
  await registerOnBackend();
  return result.user;
}

export async function signOut() {
  await loadFirebaseAuth();
  backendUser = null;
  resetToAnonName();
  await firebaseSignOut(auth);
}

async function deleteAccountOnBackend() {
  const res = await authFetch(`${apiUrl}/me`, { method: 'DELETE' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || 'failed to delete account');
  }
}

function clearAccountLocalData() {
  try { localStorage.removeItem('likedSightings'); } catch (_) {}
}

export async function resetPassword(email) {
  await loadFirebaseAuth();
  await sendPasswordResetEmail(auth, email);
}

function syncSocketUserName() {
  const name = getDisplayName();
  socket.emit('change_display_name', { newName: name });
}

function updateDisplayNameUI() {
  const el = document.querySelector('.user-display-name');
  if (el) {
    el.textContent = getDisplayName();
    if (isLoggedIn()) {
      el.classList.add('is-logged-in');
      el.classList.remove('is-guest');
    } else {
      el.classList.remove('is-logged-in');
      el.classList.add('is-guest');
    }
  }
}

// ── Auth Modal ──

let modalElement = null;

function getModal() {
  if (!modalElement) {
    modalElement = document.getElementById('authModal');
  }
  return modalElement;
}

export function openAuthModal({ message } = {}) {
  const modal = getModal();
  if (!modal) return;
  modal.classList.add('active');
  modalOpen();
  resetModalState();
  if (message) {
    const subtitle = modal.querySelector('.auth-subtitle');
    if (subtitle) subtitle.textContent = message;
  }
}

export function closeAuthModal() {
  const modal = getModal();
  if (!modal) return;
  modal.classList.remove('active');
  modalClose();
}

function resetModalState() {
  const modal = getModal();
  if (!modal) return;

  const emailSection = modal.querySelector('.auth-email-section');
  const mainButtons = modal.querySelector('.auth-main-buttons');
  const errorEl = modal.querySelector('.auth-error');
  const successEl = modal.querySelector('.auth-success');
  const subtitle = modal.querySelector('.auth-subtitle');

  const forgotSection = modal.querySelector('.auth-forgot-section');

  if (emailSection) emailSection.style.display = 'none';
  if (forgotSection) forgotSection.style.display = 'none';
  if (mainButtons) mainButtons.style.display = '';
  if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
  if (successEl) { successEl.textContent = ''; successEl.style.display = 'none'; }
  if (subtitle) subtitle.textContent = 'sign in to unlock extra features';

  const form = modal.querySelector('.auth-email-form');
  if (form) form.reset();

  const forgotForm = modal.querySelector('.auth-forgot-form');
  if (forgotForm) forgotForm.reset();

  const submitBtn = modal.querySelector('.auth-email-submit');
  if (submitBtn) submitBtn.disabled = false;
}

function showAuthError(msg) {
  const modal = getModal();
  if (!modal) return;
  const errorEl = modal.querySelector('.auth-error');
  if (errorEl) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }
}

function showAuthSuccess(msg) {
  const modal = getModal();
  if (!modal) return;
  const successEl = modal.querySelector('.auth-success');
  if (successEl) {
    successEl.textContent = msg;
    successEl.style.display = 'block';
  }
}

function setLoading(button, loading) {
  if (loading) {
    button.disabled = true;
    button.dataset.originalHtml = button.innerHTML;
    button.innerHTML = '<span class="spinner spinner--small"></span>';
  } else {
    button.disabled = false;
    if (button.dataset.originalHtml) {
      button.innerHTML = button.dataset.originalHtml;
    }
  }
}

export function initAuthModal() {
  const modal = getModal();
  if (!modal) return;

  let isRegisterMode = false;

  const closeBtn = modal.querySelector('.auth-close');
  const googleBtn = modal.querySelector('.auth-google-btn');
  const emailBtn = modal.querySelector('.auth-email-btn');
  const emailSection = modal.querySelector('.auth-email-section');
  const mainButtons = modal.querySelector('.auth-main-buttons');
  const emailForm = modal.querySelector('.auth-email-form');
  const emailToggle = modal.querySelector('.auth-email-toggle');
  const emailSubmitBtn = modal.querySelector('.auth-email-submit');
  const emailToggleLink = modal.querySelector('.auth-email-toggle-link');
  const emailBackBtn = modal.querySelector('.auth-email-back');
  const confirmField = modal.querySelector('.auth-confirm-field');
  const emailTitle = modal.querySelector('.auth-email-title');

  const forgotLinkEl = modal.querySelector('.auth-forgot-link');

  function updateEmailMode() {
    if (isRegisterMode) {
      if (emailTitle) emailTitle.textContent = 'create account';
      if (emailSubmitBtn) emailSubmitBtn.textContent = 'register';
      if (emailToggle) emailToggle.innerHTML = 'already have an account? <a href="#" class="auth-email-toggle-link">sign in</a>';
      if (confirmField) confirmField.style.display = '';
      if (forgotLinkEl) forgotLinkEl.style.display = 'none';
    } else {
      if (emailTitle) emailTitle.textContent = 'sign in with email';
      if (emailSubmitBtn) emailSubmitBtn.textContent = 'sign in';
      if (emailToggle) emailToggle.innerHTML = 'don\'t have an account? <a href="#" class="auth-email-toggle-link">register</a>';
      if (confirmField) confirmField.style.display = 'none';
      if (forgotLinkEl) forgotLinkEl.style.display = '';
    }
    const newToggleLink = modal.querySelector('.auth-email-toggle-link');
    if (newToggleLink) {
      newToggleLink.addEventListener('click', (e) => {
        e.preventDefault();
        isRegisterMode = !isRegisterMode;
        updateEmailMode();
      });
    }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', closeAuthModal);
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeAuthModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeAuthModal();
    }
  });

  if (googleBtn) {
    googleBtn.addEventListener('click', async () => {
      const errorEl = modal.querySelector('.auth-error');
      if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
      setLoading(googleBtn, true);
      try {
        await signInWithGoogle();
        closeAuthModal();
        openProfileModal();
      } catch (err) {
        if (err.code !== 'auth/popup-closed-by-user') {
          showAuthError(friendlyError(err));
        }
      } finally {
        setLoading(googleBtn, false);
      }
    });
  }

  if (emailBtn) {
    emailBtn.addEventListener('click', () => {
      mainButtons.style.display = 'none';
      emailSection.style.display = '';
      isRegisterMode = false;
      updateEmailMode();
    });
  }

  if (emailBackBtn) {
    emailBackBtn.addEventListener('click', () => {
      emailSection.style.display = 'none';
      mainButtons.style.display = '';
      const errorEl = modal.querySelector('.auth-error');
      if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
    });
  }

  if (emailForm) {
    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errorEl = modal.querySelector('.auth-error');
      if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }

      const email = emailForm.querySelector('.auth-email-input')?.value.trim();
      const password = emailForm.querySelector('.auth-password-input')?.value;
      const confirmPassword = emailForm.querySelector('.auth-confirm-password-input')?.value;

      if (!email || !password) {
        showAuthError('please fill in all fields');
        return;
      }

      if (isRegisterMode && password.length < 6) {
        showAuthError('password must be at least 6 characters');
        return;
      }

      if (isRegisterMode && password !== confirmPassword) {
        showAuthError('passwords do not match');
        return;
      }

      setLoading(emailSubmitBtn, true);

      try {
        if (isRegisterMode) {
          await registerWithEmail(email, password);
          showAuthSuccess('account created! check your email to verify');
          setTimeout(() => {
            closeAuthModal();
            openProfileModal();
          }, 2000);
        } else {
          await loginWithEmail(email, password);
          closeAuthModal();
          openProfileModal();
        }
      } catch (err) {
        showAuthError(friendlyError(err));
      } finally {
        setLoading(emailSubmitBtn, false);
      }
    });
  }

  updateEmailMode();

  modal.querySelectorAll('.auth-password-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.previousElementSibling;
      if (!input) return;
      const hidden = input.type === 'password';
      input.type = hidden ? 'text' : 'password';
      btn.textContent = hidden ? '[hide]' : '[show]';
      btn.setAttribute('aria-label', hidden ? 'Hide password' : 'Show password');
    });
  });

  // ── Forgot Password ──
  const forgotLink = modal.querySelector('.auth-forgot-link');
  const forgotSection = modal.querySelector('.auth-forgot-section');
  const forgotBackBtn = modal.querySelector('.auth-forgot-back');
  const forgotForm = modal.querySelector('.auth-forgot-form');
  const forgotSubmitBtn = modal.querySelector('.auth-forgot-submit');

  function showForgotView() {
    if (emailSection) emailSection.style.display = 'none';
    if (forgotSection) forgotSection.style.display = '';
    const errorEl = modal.querySelector('.auth-error');
    const successEl = modal.querySelector('.auth-success');
    if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
    if (successEl) { successEl.textContent = ''; successEl.style.display = 'none'; }
  }

  function hideForgotView() {
    if (forgotSection) forgotSection.style.display = 'none';
    if (emailSection) emailSection.style.display = '';
    const errorEl = modal.querySelector('.auth-error');
    const successEl = modal.querySelector('.auth-success');
    if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
    if (successEl) { successEl.textContent = ''; successEl.style.display = 'none'; }
  }

  if (forgotLink) {
    forgotLink.addEventListener('click', (e) => {
      e.preventDefault();
      showForgotView();
    });
  }

  if (forgotBackBtn) {
    forgotBackBtn.addEventListener('click', hideForgotView);
  }

  if (forgotForm) {
    forgotForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errorEl = modal.querySelector('.auth-error');
      const successEl = modal.querySelector('.auth-success');
      if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
      if (successEl) { successEl.textContent = ''; successEl.style.display = 'none'; }

      const email = forgotForm.querySelector('.auth-forgot-email-input')?.value.trim();
      if (!email) {
        showAuthError('please enter your email');
        return;
      }

      setLoading(forgotSubmitBtn, true);

      try {
        await resetPassword(email);
        showAuthSuccess('reset email sent! check your inbox');
      } catch (err) {
        showAuthError(friendlyError(err));
      } finally {
        setLoading(forgotSubmitBtn, false);
      }
    });
  }
}

function friendlyError(err) {
  const code = err?.code || '';
  const map = {
    'auth/email-already-in-use': 'this email is already registered',
    'auth/invalid-email': 'invalid email address',
    'auth/user-not-found': 'no account found with this email',
    'auth/wrong-password': 'incorrect password',
    'auth/invalid-credential': 'incorrect email or password',
    'auth/too-many-requests': 'too many attempts, please try again later',
    'auth/weak-password': 'password must be at least 6 characters',
    'auth/network-request-failed': 'network error, please check your connection',
    'auth/popup-blocked': 'popup was blocked, please allow popups and try again',
  };
  return map[code] || err?.message || 'something went wrong, please try again';
}

function stripEmptyLines(text) {
  return text.replace(/\n{2,}/g, '\n').replace(/^\n|\n$/g, '');
}

// ── Profile Modal ──

let profileModalElement = null;
const loadedProfileTabs = new Set();

function getProfileModal() {
  if (!profileModalElement) {
    profileModalElement = document.getElementById('profileModal');
  }
  return profileModalElement;
}

function updateProfileAvatar() {
  const url = backendUser?.avatar_url;

  const img = document.getElementById('profileAvatarImg');
  const placeholder = document.getElementById('profileAvatarPlaceholder');
  const removeBtn = document.getElementById('profileAvatarRemoveBtn');

  if (url) {
    if (img) { img.src = url; img.style.display = ''; }
    if (placeholder) placeholder.style.display = 'none';
    if (removeBtn) removeBtn.style.display = '';
  } else {
    if (img) { img.style.display = 'none'; img.src = ''; }
    if (placeholder) placeholder.style.display = '';
    if (removeBtn) removeBtn.style.display = 'none';
  }

  const infoImg = document.getElementById('profileInfoAvatarImg');
  const infoPlaceholder = document.getElementById('profileInfoAvatarPlaceholder');

  if (url) {
    if (infoImg) { infoImg.src = url; infoImg.style.display = ''; }
    if (infoPlaceholder) infoPlaceholder.style.display = 'none';
  } else {
    if (infoImg) { infoImg.style.display = 'none'; infoImg.src = ''; }
    if (infoPlaceholder) infoPlaceholder.style.display = '';
  }
}

function populateInfoTab() {
  const avatarImg = document.getElementById('profileInfoAvatarImg');
  const avatarPlaceholder = document.getElementById('profileInfoAvatarPlaceholder');
  const usernameEl = document.getElementById('profileInfoUsername');
  const bioTextEl = document.getElementById('profileInfoBioText');
  const bioContainer = document.getElementById('profileInfoBioContainer');

  const url = backendUser?.avatar_url;
  if (url && avatarImg) {
    avatarImg.src = url;
    avatarImg.style.display = '';
    if (avatarPlaceholder) avatarPlaceholder.style.display = 'none';
  } else {
    if (avatarImg) { avatarImg.style.display = 'none'; avatarImg.src = ''; }
    if (avatarPlaceholder) avatarPlaceholder.style.display = '';
  }

  if (usernameEl) usernameEl.textContent = getDisplayName();

  const bio = stripEmptyLines(backendUser?.bio || '');
  if (bio) {
    if (bioTextEl) bioTextEl.textContent = bio;
    if (bioContainer) { bioContainer.style.display = ''; bioContainer.classList.add('has-bio'); }
  } else {
    if (bioTextEl) bioTextEl.textContent = '';
    if (bioContainer) { bioContainer.style.display = 'none'; bioContainer.classList.remove('has-bio'); }
  }

  renderAdminRooms('profileInfoAdminRooms', backendUser?.room_admin_users);

  const joinedEl = document.getElementById('profileInfoJoined');
  if (joinedEl && backendUser?.created_at) {
    joinedEl.textContent = formatJoinedLine(backendUser);
  } else if (joinedEl) {
    joinedEl.textContent = '';
  }
}

async function loadInfoTab() {
  const skeleton = document.getElementById('profileInfoSkeleton');
  const content = document.getElementById('profileInfoContent');

  if (backendUser) {
    populateInfoTab();
    if (skeleton) skeleton.style.display = 'none';
    if (content) content.style.display = '';
    fetchAndRenderInlineStats(backendUser?.id, 'profileInfoStatsSummary', null, 'profile');
    return;
  }

  if (skeleton) skeleton.style.display = '';
  if (content) content.style.display = 'none';

  await fetchMe();

  populateInfoTab();
  if (skeleton) skeleton.style.display = 'none';
  if (content) content.style.display = '';
  fetchAndRenderInlineStats(backendUser?.id, 'profileInfoStatsSummary', null, 'profile');
}

function buildStatsSummaryHTML(total, kg, feedRank) {
  const podiumClass = feedRank === 1
    ? 'rank-1'
    : feedRank === 2
      ? 'rank-2'
      : feedRank === 3
        ? 'rank-3'
        : (feedRank && feedRank <= 10)
          ? 'rank-top10'
          : '';
  const rankRow = feedRank
    ? `<div class="profile-stats-row profile-stats-row-rank">
         <span class="profile-stats-row-label">Rank</span>
         <span class="profile-stats-row-value"><strong>#${feedRank}</strong></span>
       </div>`
    : '';
  return (
    `<div class="profile-stats-panel ${podiumClass}">` +
      `<div class="profile-stats-row">` +
        `<span class="profile-stats-row-label">Kibble Dispensed</span>` +
        `<span class="profile-stats-row-value"><strong>${total}</strong> time${total !== 1 ? 's' : ''}<span class="profile-stats-dot">·</span><strong>${kg}</strong> kg</span>` +
      `</div>` +
      rankRow +
    `</div>`
  );
}

function roomChipHues(rooms) {
  let h = 2166136261;
  for (let i = 0; i < rooms[0].length; i++) h = Math.imul(h ^ rooms[0].charCodeAt(i), 16777619);
  const start = (h >>> 0) % 360;
  return rooms.map((_, i) => (start + i * 137.5) % 360);
}

function buildAdminRoomsHTML(roomAdminUsers) {
  if (!Array.isArray(roomAdminUsers) || roomAdminUsers.length === 0) return '';
  const ownerRooms = roomAdminUsers.filter(
    (e) => e?.room_name && Array.isArray(e?.roles) && e.roles.includes('ROOM_OWNER')
  );
  if (ownerRooms.length === 0) return '';
  const hues = roomChipHues(ownerRooms.map((e) => e.room_name));
  const chips = ownerRooms
    .map((e, i) => {
      return `<span class="profile-admin-room-chip" role="link" tabindex="0" data-room="${escapeAttr(e.room_name)}" aria-label="Open ${escapeAttr(e.room_name)} stream" style="--chip-hue:${hues[i]}">${escapeAttr(e.room_name)}</span>`;
    })
    .join('');
  return `<div class="profile-admin-rooms-panel"><span class="profile-admin-rooms-label">> Owner of:</span>${chips}</div>`;
}

function navigateToAdminRoom(chip) {
  const room = chip?.dataset?.room;
  if (!room) return;
  closeProfileModal();
  closeUserCard();
  router.navigate(`/${encodeURIComponent(room)}`);
}

function renderAdminRooms(containerId, roomAdminUsers) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.innerHTML = buildAdminRoomsHTML(roomAdminUsers);

  if (!el.dataset.roomNavInstalled) {
    el.dataset.roomNavInstalled = '1';
    el.addEventListener('click', (e) => {
      const chip = e.target.closest('.profile-admin-room-chip');
      if (chip && el.contains(chip)) navigateToAdminRoom(chip);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const chip = e.target.closest('.profile-admin-room-chip');
      if (chip && el.contains(chip)) {
        e.preventDefault();
        navigateToAdminRoom(chip);
      }
    });
  }
}

const userStatsCache = new Map();

function fetchUserStats(userId, cacheKey) {
  if (!userId || !cacheKey) return Promise.resolve(null);
  const cached = userStatsCache.get(cacheKey);
  if (cached && cached.userId === userId) return cached.promise;

  const promise = fetch(`${apiUrl}/users/${userId}/stats`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  userStatsCache.set(cacheKey, { userId, promise });
  return promise;
}

function clearUserStatsCache(cacheKey) {
  userStatsCache.delete(cacheKey);
}

/** One color for all room bars (matches --main-accent-color when set; see style.css for same fallback). */
const PROFILE_BAR_COLOR = 'var(--main-accent-color,rgba(255, 255, 255, 0.79))';

function renderProfileStatsBarChart(barChartEl, perRoom) {
  const rooms = Object.entries(perRoom || {}).sort((a, b) => b[1] - a[1]);
  if (!barChartEl || rooms.length === 0) return;
  barChartEl.innerHTML = '';
  const maxCount = Math.max(...rooms.map(([, v]) => v), 1);
  rooms.forEach(([room, count]) => {
    const row = document.createElement('div');
    row.className = 'usercard-bar-row';

    const label = document.createElement('button');
    label.type = 'button';
    label.className = 'usercard-bar-label profile-stats-room-nav';
    label.dataset.room = room;
    label.textContent = room;
    label.setAttribute('aria-label', `Open ${room} stream`);

    const track = document.createElement('div');
    track.className = 'usercard-bar-track';

    const fill = document.createElement('div');
    fill.className = 'usercard-bar-fill';
    fill.style.width = '0%';
    fill.style.setProperty('--bar-color', PROFILE_BAR_COLOR);
    track.appendChild(fill);

    const value = document.createElement('span');
    value.className = 'usercard-bar-value';
    value.textContent = count;

    row.append(label, track, value);
    barChartEl.appendChild(row);

    requestAnimationFrame(() => {
      fill.style.width = `${(count / maxCount) * 100}%`;
    });
  });
}

const FEED_CHART_PERIODS = [
  { id: 'last_7_days', label: '7d', caption: 'in the last 7 days' },
  { id: 'last_30_days', label: '30d', caption: 'in the last 30 days' },
  { id: 'all_time', label: 'all', caption: 'in total' },
];

/** Padding in CSS px; the viewBox matches the plot's pixel size so labels keep a constant size. */
const FEED_CHART_PAD = { left: 28, right: 10, top: 12, bottom: 22 };
const FEED_CHART_FALLBACK_WIDTH = 320;

function feedChartHeight(width) {
  return Math.round(Math.min(200, Math.max(120, width * 0.38)));
}

const feedActivityCache = new Map();

function fetchFeedActivity(userId, period, cacheKey) {
  const key = `${cacheKey}|${period}`;
  const cached = feedActivityCache.get(key);
  if (cached && cached.userId === userId) return cached.promise;

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const promise = fetch(`${apiUrl}/users/${userId}/feed_activity?period=${period}&tz=${encodeURIComponent(tz)}`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  feedActivityCache.set(key, { userId, promise });
  return promise;
}

function clearFeedActivityCache(cacheKey) {
  for (const key of feedActivityCache.keys()) {
    if (key.startsWith(`${cacheKey}|`)) feedActivityCache.delete(key);
  }
}

/** Bucket dates are plain YYYY-MM-DD in the requested timezone, so parse them as local dates. */
function parseBucketDate(dateString) {
  const [y, m, d] = String(dateString || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatBucketLabel(dateString, interval, { long = false } = {}) {
  const d = parseBucketDate(dateString);
  if (!d) return '';
  const mon = d.toLocaleDateString('en-US', { month: 'short' }).toLowerCase();
  if (interval === 'month') return long ? `${mon} ${d.getFullYear()}` : mon;
  return long ? `${mon} ${d.getDate()}` : String(d.getDate());
}

/** Tick indices, at most 7 of them, always including the first and last bucket. */
function feedChartTickIndices(n) {
  const step = Math.max(1, Math.ceil(n / 7));
  const ticks = [];
  for (let i = 0; i < n; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== n - 1) ticks.push(n - 1);
  return ticks;
}

function buildFeedChartTicksHTML(points, tickIndices, interval, y) {
  let prev = null;
  return tickIndices
    .map((i, order) => {
      const d = parseBucketDate(points[i].date);
      const unit = interval === 'month' ? d?.getFullYear() : d?.getMonth();
      const withUnit = order === 0 || unit !== prev;
      prev = unit;
      const label = formatBucketLabel(points[i].date, interval, { long: withUnit });
      const anchor = order === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle';
      const x = order === 0 ? points[i].x - 8 : i === points.length - 1 ? points[i].x + 8 : points[i].x;
      return `<text class="feed-chart-axis-label" x="${x.toFixed(2)}" y="${y}" text-anchor="${anchor}">${label}</text>`;
    })
    .join('');
}

function buildFeedChartSvg(buckets, interval, w) {
  const h = feedChartHeight(w);
  const { left: padL, right: padR, top: padT, bottom: padB } = FEED_CHART_PAD;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const n = buckets.length;
  const max = Math.max(...buckets.map((b) => Number(b.count) || 0), 1);
  const baselineY = padT + innerH;

  const points = buckets.map((b, i) => {
    const count = Number(b.count) || 0;
    return {
      date: b.date,
      count,
      x: n === 1 ? padL + innerW / 2 : padL + (i * innerW) / (n - 1),
      y: baselineY - (count / max) * innerH,
    };
  });

  const coords = points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`);
  const area =
    `M${points[0].x.toFixed(2)},${baselineY} L` +
    coords.join(' L') +
    ` L${points[n - 1].x.toFixed(2)},${baselineY} Z`;

  const dots = n <= 14
    ? points.map((p) => `<circle class="feed-chart-dot" cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="2.5" />`).join('')
    : '';

  const svg =
    `<svg class="feed-chart-svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" role="img" aria-label="Feeds per ${interval}">` +
      `<line class="feed-chart-grid" x1="${padL}" y1="${padT}" x2="${w - padR}" y2="${padT}" />` +
      `<line class="feed-chart-grid" x1="${padL}" y1="${baselineY}" x2="${w - padR}" y2="${baselineY}" />` +
      `<text class="feed-chart-axis-label" x="${padL - 6}" y="${padT + 4}" text-anchor="end">${max}</text>` +
      `<text class="feed-chart-axis-label" x="${padL - 6}" y="${baselineY + 4}" text-anchor="end">0</text>` +
      buildFeedChartTicksHTML(points, feedChartTickIndices(n), interval, h - 6) +
      `<path class="feed-chart-area" d="${area}" />` +
      `<polyline class="feed-chart-line" points="${coords.join(' ')}" />` +
      dots +
      `<line class="feed-chart-cursor" y1="${padT}" y2="${baselineY}" style="display:none" />` +
      `<circle class="feed-chart-active-dot" r="4" style="display:none" />` +
    `</svg>`;

  return { svg, points };
}

function drawFeedChart(plotEl, captionEl, data, period) {
  const interval = data.interval === 'month' ? 'month' : 'day';
  const width = Math.round(plotEl.clientWidth) || FEED_CHART_FALLBACK_WIDTH;
  const { svg, points } = buildFeedChartSvg(data.buckets, interval, width);
  plotEl.style.height = `${feedChartHeight(width)}px`;
  plotEl.innerHTML = svg;

  const svgEl = plotEl.querySelector('svg');
  const cursor = svgEl.querySelector('.feed-chart-cursor');
  const activeDot = svgEl.querySelector('.feed-chart-active-dot');

  const total = Number(data.total) || 0;
  const periodCaption = FEED_CHART_PERIODS.find((p) => p.id === period)?.caption || '';
  const defaultCaption =
    `<span class="usercard-stats-caret">&gt;</span> <strong>${total}</strong> feed${total !== 1 ? 's' : ''} ${periodCaption}`;
  captionEl.innerHTML = defaultCaption;

  const setActive = (i) => {
    if (i == null) {
      cursor.style.display = 'none';
      activeDot.style.display = 'none';
      captionEl.innerHTML = defaultCaption;
      return;
    }
    const p = points[i];
    cursor.setAttribute('x1', p.x.toFixed(2));
    cursor.setAttribute('x2', p.x.toFixed(2));
    cursor.style.display = '';
    activeDot.setAttribute('cx', p.x.toFixed(2));
    activeDot.setAttribute('cy', p.y.toFixed(2));
    activeDot.style.display = '';
    const when = `${interval === 'month' ? 'in' : 'on'} ${formatBucketLabel(p.date, interval, { long: true })}`;
    captionEl.innerHTML =
      `<span class="usercard-stats-caret">&gt;</span> <strong>${p.count}</strong> feed${p.count !== 1 ? 's' : ''} ${when}`;
  };

  const indexFromEvent = (e) => {
    const rect = svgEl.getBoundingClientRect();
    if (!rect.width) return null;
    const vx = ((e.clientX - rect.left) / rect.width) * width;
    let best = 0;
    for (let i = 1; i < points.length; i++) {
      if (Math.abs(points[i].x - vx) < Math.abs(points[best].x - vx)) best = i;
    }
    return best;
  };

  svgEl.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') setActive(indexFromEvent(e));
  });
  svgEl.addEventListener('pointerdown', (e) => setActive(indexFromEvent(e)));
  svgEl.addEventListener('pointerleave', () => setActive(null));
}

const feedChartResizeObservers = new WeakMap();

function initFeedActivityChart(userId, chartEl, cacheKey) {
  if (!userId || !chartEl) return;

  chartEl.innerHTML =
    `<div class="feed-chart-head">` +
      `<span class="feed-chart-title"><span class="usercard-stats-caret">&gt;</span> Feeding activity</span>` +
      `<div class="feed-chart-periods" role="group" aria-label="Chart period">` +
        FEED_CHART_PERIODS.map((p, i) =>
          `<button type="button" class="gallery-filter-btn feed-chart-period${i === 0 ? ' active' : ''}" data-period="${p.id}">${p.label}</button>`
        ).join('') +
      `</div>` +
    `</div>` +
    `<div class="feed-chart-plot"></div>` +
    `<div class="feed-chart-caption"></div>`;

  const plotEl = chartEl.querySelector('.feed-chart-plot');
  const captionEl = chartEl.querySelector('.feed-chart-caption');
  plotEl.style.height = `${feedChartHeight(Math.round(plotEl.clientWidth) || FEED_CHART_FALLBACK_WIDTH)}px`;

  let requestToken = 0;
  let drawn = null;
  let drawnWidth = 0;

  const draw = (data, period) => {
    drawn = { data, period };
    drawnWidth = Math.round(plotEl.clientWidth);
    drawFeedChart(plotEl, captionEl, data, period);
  };

  const load = async (period) => {
    const token = ++requestToken;
    chartEl.classList.add('feed-chart--loading');
    const data = await fetchFeedActivity(userId, period, cacheKey);
    if (token !== requestToken) return;
    chartEl.classList.remove('feed-chart--loading');

    if (!data || !Array.isArray(data.buckets) || data.buckets.length === 0) {
      drawn = null;
      plotEl.innerHTML = '';
      captionEl.innerHTML = '';
      return;
    }
    draw(data, period);
  };

  // The chart is laid out in pixels, so it has to be rebuilt whenever the plot width changes.
  feedChartResizeObservers.get(chartEl)?.disconnect();
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => {
      if (!drawn) return;
      const width = Math.round(plotEl.clientWidth);
      if (!width || width === drawnWidth) return;
      draw(drawn.data, drawn.period);
    });
    feedChartResizeObservers.set(chartEl, observer);
    observer.observe(plotEl);
  }

  chartEl.querySelector('.feed-chart-periods').addEventListener('click', (e) => {
    const btn = e.target.closest('.feed-chart-period');
    if (!btn || btn.classList.contains('active')) return;
    chartEl.querySelectorAll('.feed-chart-period').forEach((b) => b.classList.toggle('active', b === btn));
    load(btn.dataset.period);
  });

  load(FEED_CHART_PERIODS[0].id);
}

function buildInlineStatsSkeletonHTML() {
  return (
    `<div class="profile-stats-panel profile-stats-panel--skeleton" aria-hidden="true">` +
      `<div class="profile-stats-skeleton-row">` +
        `<span class="profile-stats-skeleton-cell profile-stats-skeleton-label"></span>` +
        `<span class="profile-stats-skeleton-cell profile-stats-skeleton-value"></span>` +
      `</div>` +
      `<div class="profile-stats-skeleton-row">` +
        `<span class="profile-stats-skeleton-cell profile-stats-skeleton-label profile-stats-skeleton-label--sm"></span>` +
        `<span class="profile-stats-skeleton-cell profile-stats-skeleton-value profile-stats-skeleton-value--sm"></span>` +
      `</div>` +
    `</div>`
  );
}

async function fetchAndRenderInlineStats(userId, summaryId, barChartId, cacheKey) {
  const summaryEl = document.getElementById(summaryId);
  const barChartEl = barChartId ? document.getElementById(barChartId) : null;
  if (!userId || !summaryEl) return;

  try {
    const showSkeleton = summaryId === 'profileInfoStatsSummary';
    summaryEl.innerHTML = showSkeleton ? buildInlineStatsSkeletonHTML() : '';
    if (barChartEl) barChartEl.innerHTML = '';

    if (showSkeleton) {
      // If the stats request resolves "immediately" (cached/fast), the browser may never paint the
      // skeleton before we replace it. Yield one frame so the skeleton can actually appear.
      await new Promise(requestAnimationFrame);
    }

    const data = await fetchUserStats(userId, cacheKey);
    if (!data) {
      summaryEl.innerHTML = '';
      return;
    }

    const feedEvents = data.stats?.feed_events;
    const feedRank = data.stats?.feed_rank;
    if (!feedEvents || feedEvents.total === 0) {
      summaryEl.innerHTML = '';
      return;
    }

    const total = feedEvents.total;
    const kg = (total * 5 / 1000).toFixed(2).replace(/\.00$/, '');

    summaryEl.innerHTML = buildStatsSummaryHTML(total, kg, feedRank);

    const perRoom = feedEvents.per_room || {};
    const rooms = Object.entries(perRoom).sort((a, b) => b[1] - a[1]);

    if (barChartEl && rooms.length > 0) {
      renderProfileStatsBarChart(barChartEl, perRoom);
    }
  } catch (_) {
    /* stats are non-critical */
  }
}

async function loadStatsTab(userId, skeletonId, contentId, summaryId, barChartId, chartId, emptyId, recentId, cacheKey) {
  const skeleton = document.getElementById(skeletonId);
  const content = document.getElementById(contentId);
  const summaryEl = document.getElementById(summaryId);
  const barChartEl = document.getElementById(barChartId);
  const chartEl = chartId ? document.getElementById(chartId) : null;
  const emptyEl = document.getElementById(emptyId);
  const recentEl = recentId ? document.getElementById(recentId) : null;

  if (!userId) {
    if (skeleton) skeleton.style.display = 'none';
    if (summaryEl) summaryEl.innerHTML = '';
    if (barChartEl) barChartEl.innerHTML = '';
    if (chartEl) chartEl.innerHTML = '';
    if (recentEl) recentEl.innerHTML = '';
    if (emptyEl) { emptyEl.style.display = ''; }
    if (content) content.style.display = '';
    return;
  }

  if (skeleton) skeleton.style.display = '';
  if (content) content.style.display = 'none';
  if (summaryEl) summaryEl.innerHTML = '';
  if (barChartEl) barChartEl.innerHTML = '';
  if (chartEl) chartEl.innerHTML = '';
  if (recentEl) recentEl.innerHTML = '';
  if (emptyEl) emptyEl.style.display = 'none';

  try {
    const data = await fetchUserStats(userId, cacheKey);
    if (!data) throw new Error('failed to load stats');

    const feedEvents = data.stats?.feed_events;
    const feedRank = data.stats?.feed_rank;

    if (!feedEvents || (feedEvents.total === 0)) {
      if (skeleton) skeleton.style.display = 'none';
      if (summaryEl) summaryEl.innerHTML = '';
      if (barChartEl) barChartEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      if (content) content.style.display = '';
      return;
    }

    const total = feedEvents.total;
    const kg = (total * 5 / 1000).toFixed(2).replace(/\.00$/, '');
    const rankHtml = feedRank ? `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Top <strong>#${feedRank}</strong> in kibble dispensed</div>` : '';

    if (summaryEl) {
      summaryEl.innerHTML =
        `<div class="profile-stats-summary-line"><span class="usercard-stats-caret">&gt;</span> Dispensed food <strong>${total}</strong> time${total !== 1 ? 's' : ''} (<strong>${kg} kg</strong>)</div>` +
        rankHtml;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    const perRoom = feedEvents.per_room || {};
    const rooms = Object.entries(perRoom).sort((a, b) => b[1] - a[1]);

    if (barChartEl) {
      barChartEl.innerHTML = '';
      if (rooms.length > 0) {
        renderProfileStatsBarChart(barChartEl, perRoom);
      }
    }

    if (recentEl) loadRecentFeedEvents(userId, recentEl);

    if (skeleton) skeleton.style.display = 'none';
    if (content) content.style.display = '';

    // Needs a visible container: the chart is laid out from the plot's measured pixel width.
    if (chartEl) initFeedActivityChart(userId, chartEl, cacheKey);
  } catch (_) {
    if (skeleton) skeleton.style.display = 'none';
    if (summaryEl) summaryEl.innerHTML = '';
    if (barChartEl) barChartEl.innerHTML = '';
    if (chartEl) chartEl.innerHTML = '';
    if (recentEl) recentEl.innerHTML = '';
    if (emptyEl) emptyEl.style.display = '';
    if (content) content.style.display = '';
  }
}

const RECENT_FEED_PER_PAGE = 6;
const recentFeedPaginationByContainer = new WeakMap();

function getRecentFeedPagination(recentEl) {
  let s = recentFeedPaginationByContainer.get(recentEl);
  if (!s) {
    s = { userId: null, page: 0, totalPages: 0, loading: false };
    recentFeedPaginationByContainer.set(recentEl, s);
  }
  return s;
}

function buildRecentFeedItemHtml(ev) {
  const rawRoom = ev.room_name;
  const when = escapeAttr(formatFeedEventTime(ev.created_at));
  const display = rawRoom && rawRoom !== '?' ? rawRoom : (rawRoom || '?');
  const roomDisplay = escapeAttr(display);
  let roomHtml;
  if (rawRoom && rawRoom !== '?') {
    const safeRoom = escapeAttr(rawRoom);
    const label = escapeAttr(`Open ${rawRoom} stream`);
    roomHtml =
      `<button type="button" class="profile-stats-recent-room profile-stats-room-nav" data-room="${safeRoom}" aria-label="${label}">${roomDisplay}</button>`;
  } else {
    roomHtml = `<span class="profile-stats-recent-room">${roomDisplay}</span>`;
  }
  return (
    `<li class="profile-stats-recent-item">` +
    `${roomHtml}` +
    `<span class="profile-stats-recent-dots" aria-hidden="true"></span>` +
    `<span class="profile-stats-recent-when">${when}</span>` +
    `</li>`
  );
}

function syncRecentFeedPaginationFromResponse(p, data, nextPage, eventsLength) {
  const resolvedPage = data.page != null ? Number(data.page) : nextPage;
  const resolvedPages = data.pages != null ? Math.max(1, Number(data.pages)) : 1;
  p.page = resolvedPage;
  p.totalPages = resolvedPages;
  if (eventsLength >= RECENT_FEED_PER_PAGE && p.page >= p.totalPages) {
    p.totalPages = p.page + 1;
  }
}

function syncMoreRowAnimationDelay(recentEl) {
  const ul = recentEl.querySelector('.profile-stats-recent-list');
  const moreLi = ul?.querySelector('.profile-stats-recent-more-wrap');
  if (!moreLi || !ul) return;
  const n = ul.querySelectorAll('.profile-stats-recent-item').length;
  moreLi.style.animationDelay = `${(0.02 + n * 0.05).toFixed(2)}s`;
}

function updateRecentFeedMoreVisibility(recentEl, p) {
  const wrap = recentEl.querySelector('.profile-stats-recent-more-wrap');
  if (!wrap) return;
  if (p.page < p.totalPages) {
    syncMoreRowAnimationDelay(recentEl);
    wrap.style.display = 'flex';
  } else {
    wrap.style.display = 'none';
  }
}

async function loadRecentFeedEvents(userId, recentEl, { append = false } = {}) {
  if (!userId || !recentEl) return;

  const p = getRecentFeedPagination(recentEl);
  if (!append) {
    p.userId = userId;
    p.page = 0;
    p.totalPages = 0;
  }

  if (p.loading) return;
  if (append && p.page >= p.totalPages) return;

  const nextPage = append ? p.page + 1 : 1;
  const moreBtn = recentEl.querySelector('.profile-stats-recent-more');

  p.loading = true;
  if (moreBtn) moreBtn.disabled = true;

  try {
    const res = await fetch(
      `${apiUrl}/users/${userId}/feed_events?page=${nextPage}&per_page=${RECENT_FEED_PER_PAGE}`,
    );
    if (!res.ok) return;

    const data = await res.json();
    const events = Array.isArray(data.feed_events) ? data.feed_events : [];

    if (!append && events.length === 0) return;

    syncRecentFeedPaginationFromResponse(p, data, nextPage, events.length);

    if (!append) {
      const items = events.map(buildRecentFeedItemHtml).join('');
      recentEl.innerHTML =
        `<div class="profile-stats-separator"></div>` +
        `<div class="profile-stats-recent-title"><span class="usercard-stats-caret">&gt;</span> Recent feeding history:</div>` +
        `<ul class="profile-stats-recent-list">` +
        `${items}` +
        `<li class="profile-stats-recent-more-wrap" style="display: none">` +
        `<button type="button" class="profile-stats-recent-more">more</button>` +
        `</li>` +
        `</ul>`;

      const btn = recentEl.querySelector('.profile-stats-recent-more');
      if (btn) {
        btn.addEventListener('click', () => {
          loadRecentFeedEvents(userId, recentEl, { append: true });
        });
      }
      updateRecentFeedMoreVisibility(recentEl, p);
    } else if (events.length === 0) {
      p.totalPages = p.page;
      updateRecentFeedMoreVisibility(recentEl, p);
    } else {
      const ul = recentEl.querySelector('.profile-stats-recent-list');
      const moreLi = ul?.querySelector('.profile-stats-recent-more-wrap');
      const chunk = events.map(buildRecentFeedItemHtml).join('');
      if (ul) {
        if (moreLi) moreLi.insertAdjacentHTML('beforebegin', chunk);
        else ul.insertAdjacentHTML('beforeend', chunk);
      }
      updateRecentFeedMoreVisibility(recentEl, p);
    }
  } catch (_) {
    /* recent feeds are non-critical */
  } finally {
    p.loading = false;
    const b = recentEl.querySelector('.profile-stats-recent-more');
    if (b) b.disabled = false;
  }
}

function formatFeedEventTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((today - day) / 86400000);
  let h = d.getHours();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  const time = `${h}:${min}${ampm}`;
  if (diff === 0) return `today ${time}`;
  if (diff === 1) return `yesterday ${time}`;
  const mon = d.toLocaleDateString('en-US', { month: 'short' }).toLowerCase();
  return d.getFullYear() === now.getFullYear()
    ? `${mon} ${d.getDate()}, ${time}`
    : `${mon} ${d.getDate()} ${d.getFullYear()}, ${time}`;
}

const likesTabState = {};

function clearLikesState(prefix) {
  for (const key of Object.keys(likesTabState)) {
    if (key.startsWith(prefix)) {
      const s = likesTabState[key];
      if (s.scrollHandler) {
        const grid = document.getElementById(`${prefix}Grid`);
        const wrapper = grid?.closest('.modal-content-wrapper');
        if (wrapper) wrapper.removeEventListener('scroll', s.scrollHandler);
      }
      delete likesTabState[key];
    }
  }
}

function escapeAttr(s = '') {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function isVideoUrl(url) {
  return /\.mp4(\?.*)?$/i.test(url);
}

function parseUTC(dateString) {
  if (!dateString) return null;
  const normalized = `${dateString.replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatJoinDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(DATE_LOCALE, { year: 'numeric', month: 'long', day: 'numeric' });
}

// "today", "yesterday" or "N days ago", counted in calendar days in the viewer's timezone
function formatLastSeen(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Rounded so a DST change in between doesn't leave a 23 or 25 hour day
  const days = Math.round((startOfToday - startOfThatDay) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function formatJoinedLine(user) {
  const lastSeen = formatLastSeen(user.last_seen_at);
  return `> Joined ${formatJoinDate(user.created_at)}${lastSeen ? `, last seen ${lastSeen}` : ''}`;
}

function formatLikeTime(dateString) {
  const d = parseUTC(dateString);
  if (!d) return '';
  let h = d.getHours();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${min}${ampm}`;
}

function getLikeDayKey(dateString) {
  const d = parseUTC(dateString);
  if (!d) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function formatLikeDayLabel(dateString) {
  const d = parseUTC(dateString);
  if (!d) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  const mon = d.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
  return d.getFullYear() === now.getFullYear()
    ? `${mon} ${d.getDate()}`
    : `${mon} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatDurationBadge(seconds) {
  if (seconds == null || !isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function createLikeDayGroup(dateString) {
  const group = document.createElement('div');
  group.className = 'gallery-day-group';
  group.dataset.dayKey = getLikeDayKey(dateString) || '';

  const sep = document.createElement('div');
  sep.className = 'gallery-day-separator';
  sep.innerHTML = `<span class="gallery-day-label">${escapeAttr(formatLikeDayLabel(dateString))}</span>`;

  const cards = document.createElement('div');
  cards.className = 'gallery-day-cards';

  group.appendChild(sep);
  group.appendChild(cards);
  return { group, cards };
}

function createLikeCard(sighting, animIndex) {
  const card = document.createElement('article');
  card.className = 'gallery-card gallery-card--animate';
  card.style.animationDelay = `${animIndex * 40}ms`;
  card.setAttribute('tabindex', '0');

  const video = isVideoUrl(sighting.url);
  const hasThumbnail = video && sighting.thumbnail_url;

  if (hasThumbnail) {
    const dur = formatDurationBadge(sighting.duration);
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner"><div class="spinner spinner--thumb"></div></div>
        <img class="gallery-thumb" src="${escapeAttr(sighting.thumbnail_url)}" alt="Cat sighting" loading="lazy">
        <div class="gallery-thumb-play">▶ <span class="gallery-thumb-duration">${escapeAttr(dur)}</span></div>
      </div>
      <div class="gallery-card-date">${escapeAttr(formatLikeTime(sighting.created_at))}</div>
    `;
    const img = card.querySelector('img.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');
    img.onload = () => spinner.classList.add('is-hidden');
    img.onerror = () => { spinner.classList.add('is-hidden'); img.classList.add('gallery-thumb--fallback'); };
  } else if (video) {
    const dur = formatDurationBadge(sighting.duration);
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner"><div class="spinner spinner--thumb"></div></div>
        <video class="gallery-thumb" src="${escapeAttr(sighting.url)}" preload="metadata" muted playsinline></video>
        <div class="gallery-thumb-play">▶ <span class="gallery-thumb-duration">${escapeAttr(dur)}</span></div>
      </div>
      <div class="gallery-card-date">${escapeAttr(formatLikeTime(sighting.created_at))}</div>
    `;
    const vid = card.querySelector('video.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');
    vid.addEventListener('loadedmetadata', () => {
      vid.currentTime = Math.min(2, vid.duration);
    });
    vid.addEventListener('seeked', () => spinner.classList.add('is-hidden'), { once: true });
    vid.onerror = () => spinner.classList.add('is-hidden');
  } else {
    card.innerHTML = `
      <div class="gallery-thumb-wrapper">
        <div class="gallery-thumb-spinner"><div class="spinner spinner--thumb"></div></div>
        <img class="gallery-thumb" src="${escapeAttr(sighting.url)}" alt="Cat sighting" loading="lazy">
      </div>
      <div class="gallery-card-date">${escapeAttr(formatLikeTime(sighting.created_at))}</div>
    `;
  }

  if (!video) {
    const img = card.querySelector('img.gallery-thumb');
    const spinner = card.querySelector('.gallery-thumb-spinner');
    if (img) {
      img.onload = () => spinner.classList.add('is-hidden');
      img.onerror = () => { spinner.classList.add('is-hidden'); img.classList.add('gallery-thumb--fallback'); };
    }
  }

  return card;
}

function renderLikeSightings(sightings, grid, state, animOffset) {
  sightings.forEach((sighting, i) => {
    const globalIndex = animOffset + i;
    const dayKey = getLikeDayKey(sighting.created_at);
    if (dayKey !== state.lastDay) {
      const { group, cards } = createLikeDayGroup(sighting.created_at);
      grid.appendChild(group);
      state.dayCards = cards;
      state.lastDay = dayKey;
    }
    const card = createLikeCard(sighting, globalIndex);

    const openModal = () => {
      if (window.openGalleryWithSightings) {
        window.openGalleryWithSightings(state.sightings, globalIndex);
      }
    };
    card.addEventListener('click', openModal);
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(); }
    });

    state.dayCards.appendChild(card);
  });
}

function getLikesState(prefix, userId) {
  const key = `${prefix}_${userId}`;
  if (!likesTabState[key]) {
    likesTabState[key] = {
      page: 0, totalPages: 0, sightings: [],
      sortOrder: 'newest', lastDay: null, dayCards: null,
      isLoading: false, scrollHandler: null
    };
  }
  return { key, state: likesTabState[key] };
}

function setupLikesScroll(userId, prefix, grid, loading) {
  const { state } = getLikesState(prefix, userId);
  if (state.scrollHandler) return;

  const wrapper = grid.closest('.modal-content-wrapper');
  if (!wrapper) return;

  state.scrollHandler = async () => {
    if (state.isLoading || state.page >= state.totalPages) return;
    const { scrollTop, scrollHeight, clientHeight } = wrapper;
    if (scrollTop + clientHeight < scrollHeight - 200) return;

    state.isLoading = true;
    if (loading) loading.style.display = 'flex';

    try {
      const nextPage = state.page + 1;
      const res = await fetch(
        `${apiUrl}/users/${userId}/liked_cat_sightings?page=${nextPage}&per_page=25&order_by=${state.sortOrder}`
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      state.page = data.page || nextPage;
      state.totalPages = data.pages || state.totalPages;
      const newItems = data.sightings || [];
      const offset = state.sightings.length;
      state.sightings.push(...newItems);
      renderLikeSightings(newItems, grid, state, offset);
    } catch (_) { /* non-critical */ }

    state.isLoading = false;
    if (loading) loading.style.display = 'none';
  };

  wrapper.addEventListener('scroll', state.scrollHandler);
}

function cleanupLikesScroll(prefix, userId) {
  const { state } = getLikesState(prefix, userId);
  if (state.scrollHandler) {
    const grid = document.getElementById(`${prefix}Grid`);
    const wrapper = grid?.closest('.modal-content-wrapper');
    if (wrapper) wrapper.removeEventListener('scroll', state.scrollHandler);
    state.scrollHandler = null;
  }
}

async function loadLikesTab(userId, gridId, loadingId, emptyId, filterBarId) {
  const grid = document.getElementById(gridId);
  const loading = document.getElementById(loadingId);
  const empty = document.getElementById(emptyId);
  const filterBar = filterBarId ? document.getElementById(filterBarId) : null;
  if (!grid || !userId) return;

  const prefix = gridId.replace('Grid', '');
  const { state } = getLikesState(prefix, userId);

  if (loading) loading.style.display = 'flex';
  grid.innerHTML = '';
  if (empty) empty.style.display = 'none';
  state.page = 0;
  state.totalPages = 0;
  state.sightings = [];
  state.lastDay = null;
  state.dayCards = null;
  state.isLoading = false;
  cleanupLikesScroll(prefix, userId);

  if (filterBar) {
    filterBar.querySelectorAll('.gallery-sort-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.sort === state.sortOrder);
    });
  }

  try {
    const res = await fetch(
      `${apiUrl}/users/${userId}/liked_cat_sightings?page=1&per_page=25&order_by=${state.sortOrder}`
    );
    if (!res.ok) throw new Error();
    const data = await res.json();

    state.page = data.page || 1;
    state.totalPages = data.pages || 0;
    state.sightings = data.sightings || [];

    if (loading) loading.style.display = 'none';

    if (state.sightings.length === 0) {
      if (empty) empty.style.display = 'flex';
      return;
    }

    renderLikeSightings(state.sightings, grid, state, 0);

    if (state.totalPages > 1) {
      setupLikesScroll(userId, prefix, grid, loading);
    }
  } catch (_) {
    if (loading) loading.style.display = 'none';
    if (empty) {
      empty.querySelector('p').textContent = 'failed to load likes';
      empty.style.display = 'flex';
    }
  }
}

function switchProfileTab(targetId, { force = false } = {}) {
  const modal = getProfileModal();
  if (!modal) return;

  if (!force) {
    const currentActive = modal.querySelector('.profile-tabs .modal-tab.active');
    if (currentActive?.dataset?.target === targetId) return;
  }

  const tabs = modal.querySelectorAll('.profile-tabs .modal-tab');
  const contents = modal.querySelectorAll('.modal-content-wrapper .tab-content');

  tabs.forEach(t => t.classList.remove('active'));
  contents.forEach(c => { c.classList.remove('active'); c.style.display = 'none'; });

  const targetTab = modal.querySelector(`.profile-tabs .modal-tab[data-target="${targetId}"]`);
  const targetContent = modal.querySelector(`#${targetId}`);

  if (targetTab) targetTab.classList.add('active');
  if (targetContent) { targetContent.classList.add('active'); targetContent.style.display = ''; }

  if (loadedProfileTabs.has(targetId)) return;
  loadedProfileTabs.add(targetId);

  if (targetId === 'profileInfoTab') loadInfoTab();
  if (targetId === 'profileLikesTab') {
    loadLikesTab(backendUser?.id, 'profileLikesGrid', 'profileLikesLoading', 'profileLikesEmpty');
  }
  if (targetId === 'profileStatsTab') {
    loadStatsTab(backendUser?.id, 'profileStatsSkeleton', 'profileStatsContent', 'profileStatsSummary', 'profileStatsBarChart', 'profileStatsChart', 'profileStatsEmpty', 'profileStatsRecent', 'profile');
  }
}

function showProfileView() {
  const profileView = document.getElementById('profileView');
  const settingsView = document.getElementById('profileSettingsView');
  if (profileView) profileView.style.display = '';
  if (settingsView) settingsView.classList.remove('active');
}

function showSettingsView() {
  const profileView = document.getElementById('profileView');
  const settingsView = document.getElementById('profileSettingsView');
  if (profileView) profileView.style.display = 'none';
  if (settingsView) settingsView.classList.add('active');

  const nameInput = document.getElementById('profileNameInput');
  const bioInput = document.getElementById('profileBioInput');
  const bioCharcount = document.getElementById('profileBioCharcount');
  const nameError = document.getElementById('profileNameError');
  const bioError = document.getElementById('profileBioError');

  if (nameInput) nameInput.value = getDisplayName();
  if (bioInput) bioInput.value = backendUser?.bio || '';
  if (bioCharcount) bioCharcount.textContent = `${(backendUser?.bio || '').length}/100`;
  if (nameError) { nameError.textContent = ''; nameError.style.display = 'none'; }
  if (bioError) { bioError.textContent = ''; bioError.style.display = 'none'; }

  const emailEl = document.getElementById('profileSettingsEmail');
  if (emailEl) {
    const email = backendUser?.email || currentUser?.email || '';
    emailEl.textContent = email || '—';
  }

  updateProfileAvatar();

  const dropdown = document.getElementById('profileAvatarDropdown');
  if (dropdown) dropdown.style.display = 'none';
}

export function openProfileModal() {
  const modal = getProfileModal();
  if (!modal) return;

  clearLikesState('profileLikes');
  showProfileView();

  modal.classList.add('active');
  modalOpen();

  loadedProfileTabs.clear();
  clearUserStatsCache('profile');
  clearFeedActivityCache('profile');
  switchProfileTab('profileInfoTab', { force: true });
}

export function closeProfileModal() {
  const modal = getProfileModal();
  if (!modal) return;
  modal.classList.remove('active');
  modalClose();
}

// ── Avatar Upload / Crop ──

const MAX_AVATAR_SIZE = 5 * 1024 * 1024;
let cropperInstance = null;

function showAvatarError(msg) {
  const el = document.getElementById('profileAvatarError');
  if (!el) return;
  el.textContent = msg;
  el.style.display = msg ? 'block' : 'none';
}

function setAvatarUploading(uploading) {
  const wrapper = document.getElementById('profileAvatarWrapper');
  if (!wrapper) return;
  if (uploading) {
    wrapper.classList.add('profile-avatar-uploading');
  } else {
    wrapper.classList.remove('profile-avatar-uploading');
  }
}

function openCropModal(file) {
  const modal = document.getElementById('avatarCropModal');
  const imgEl = document.getElementById('avatarCropImage');
  if (!modal || !imgEl) return;

  const url = URL.createObjectURL(file);

  if (cropperInstance) {
    cropperInstance.destroy();
    cropperInstance = null;
  }

  imgEl.src = url;
  modal.classList.add('active');

  import('cropperjs/dist/cropper.css');
  import('cropperjs').then(({ default: Cropper }) => {
    cropperInstance = new Cropper(imgEl, {
      aspectRatio: 1,
      viewMode: 1,
      dragMode: 'move',
      autoCropArea: 1,
      cropBoxResizable: true,
      cropBoxMovable: true,
      guides: false,
      center: true,
      background: false,
      responsive: true,
      ready() {
        URL.revokeObjectURL(url);
      },
    });
  });
}

function closeCropModal() {
  const modal = document.getElementById('avatarCropModal');
  if (modal) modal.classList.remove('active');

  if (cropperInstance) {
    cropperInstance.destroy();
    cropperInstance = null;
  }

  const imgEl = document.getElementById('avatarCropImage');
  if (imgEl) imgEl.src = '';
}

async function cropAndUpload() {
  if (!cropperInstance) return;

  const canvas = cropperInstance.getCroppedCanvas({
    width: 512,
    height: 512,
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high',
  });

  if (!canvas) return;

  const confirmBtn = document.getElementById('avatarCropConfirm');
  if (confirmBtn) confirmBtn.disabled = true;

  try {
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    );

    if (!blob) throw new Error('failed to generate image');

    if (blob.size > MAX_AVATAR_SIZE) {
      showAvatarError('cropped image is too large, please try a smaller selection');
      return;
    }

    closeCropModal();
    setAvatarUploading(true);
    showAvatarError('');

    const form = new FormData();
    form.append('file', blob, 'avatar.jpg');

    const res = await authFetch(`${apiUrl}/me/avatar`, {
      method: 'POST',
      body: form,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || data.error || 'upload failed');
    }

    const data = await res.json();
    if (data.user) {
      backendUser = data.user;
    } else if (data.avatar_url) {
      backendUser = { ...backendUser, avatar_url: data.avatar_url };
    }

    updateProfileAvatar();
  } catch (err) {
    showAvatarError(err.message || 'upload failed');
  } finally {
    setAvatarUploading(false);
    if (confirmBtn) confirmBtn.disabled = false;
  }
}

async function deleteAvatar() {
  setAvatarUploading(true);
  showAvatarError('');

  try {
    const res = await authFetch(`${apiUrl}/me/avatar`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || data.error || 'delete failed');
    }

    if (backendUser) {
      backendUser = { ...backendUser, avatar_url: null };
    }

    updateProfileAvatar();
  } catch (err) {
    showAvatarError(err.message || 'could not remove avatar');
  } finally {
    setAvatarUploading(false);
  }
}

function handleAvatarFileSelect(file) {
  showAvatarError('');

  if (!file || !file.type.startsWith('image/')) {
    showAvatarError('please select an image file');
    return;
  }

  if (file.size > MAX_AVATAR_SIZE) {
    showAvatarError('image must be under 5 MB');
    return;
  }

  openCropModal(file);
}

export function initProfileModal() {
  const modal = getProfileModal();
  if (!modal) return;

  const closeBtn = modal.querySelector('.profile-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeProfileModal);
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeProfileModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const confirmModal = document.getElementById('signoutConfirmModal');
      if (confirmModal?.classList.contains('active')) {
        confirmModal.classList.remove('active');
        return;
      }
      const deleteConfirmModal = document.getElementById('deleteAccountConfirmModal');
      if (deleteConfirmModal?.classList.contains('active')) {
        deleteConfirmModal.classList.remove('active');
        return;
      }
      const deleteFinalConfirmModal = document.getElementById('deleteAccountFinalConfirmModal');
      if (deleteFinalConfirmModal?.classList.contains('active')) {
        deleteFinalConfirmModal.classList.remove('active');
        return;
      }
      const cropModal = document.getElementById('avatarCropModal');
      if (cropModal?.classList.contains('active')) {
        closeCropModal();
        return;
      }
      if (modal.classList.contains('active')) {
        closeProfileModal();
      }
    }
  });

  // Sign out with confirmation
  const signOutBtn = document.getElementById('profileSignoutBtn');
  const signoutModal = document.getElementById('signoutConfirmModal');
  const signoutYes = document.getElementById('signoutConfirmYes');
  const signoutCancel = document.getElementById('signoutConfirmCancel');

  function openSignoutConfirm() {
    if (signoutModal) signoutModal.classList.add('active');
  }

  function closeSignoutConfirm() {
    if (signoutModal) signoutModal.classList.remove('active');
  }

  if (signOutBtn) {
    signOutBtn.addEventListener('click', openSignoutConfirm);
  }

  if (signoutYes) {
    signoutYes.addEventListener('click', async () => {
      closeSignoutConfirm();
      closeProfileModal();
      await signOut();
    });
  }

  if (signoutCancel) {
    signoutCancel.addEventListener('click', closeSignoutConfirm);
  }

  if (signoutModal) {
    signoutModal.addEventListener('click', (e) => {
      if (e.target === signoutModal) closeSignoutConfirm();
    });
  }

  // Delete account with confirmation
  const deleteBtn = document.getElementById('profileDeleteAccountBtn');
  const deleteModal = document.getElementById('deleteAccountConfirmModal');
  const deleteYes = document.getElementById('deleteAccountConfirmYes');
  const deleteCancel = document.getElementById('deleteAccountConfirmCancel');
  const deleteFinalModal = document.getElementById('deleteAccountFinalConfirmModal');
  const deleteFinalYes = document.getElementById('deleteAccountFinalConfirmYes');
  const deleteFinalCancel = document.getElementById('deleteAccountFinalConfirmCancel');

  function openDeleteConfirm() {
    if (deleteModal) deleteModal.classList.add('active');
  }

  function closeDeleteConfirm() {
    if (deleteModal) deleteModal.classList.remove('active');
  }

  function openDeleteFinalConfirm() {
    if (deleteFinalModal) deleteFinalModal.classList.add('active');
  }

  function closeDeleteFinalConfirm() {
    if (deleteFinalModal) deleteFinalModal.classList.remove('active');
  }

  if (deleteBtn) {
    deleteBtn.addEventListener('click', openDeleteConfirm);
  }

  if (deleteCancel) {
    deleteCancel.addEventListener('click', closeDeleteConfirm);
  }

  if (deleteYes) {
    deleteYes.addEventListener('click', () => {
      closeDeleteConfirm();
      openDeleteFinalConfirm();
    });
  }

  if (deleteFinalCancel) {
    deleteFinalCancel.addEventListener('click', closeDeleteFinalConfirm);
  }

  if (deleteFinalYes) {
    deleteFinalYes.addEventListener('click', async () => {
      closeDeleteFinalConfirm();
      closeProfileModal();
      try {
        await deleteAccountOnBackend();
      } finally {
        clearAccountLocalData();
        await signOut();
      }
    });
  }

  if (deleteModal) {
    deleteModal.addEventListener('click', (e) => {
      if (e.target === deleteModal) closeDeleteConfirm();
    });
  }

  if (deleteFinalModal) {
    deleteFinalModal.addEventListener('click', (e) => {
      if (e.target === deleteFinalModal) closeDeleteFinalConfirm();
    });
  }

  // Settings toggle
  const settingsBtn = document.getElementById('profileSettingsBtn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', showSettingsView);
  }

  const settingsBackBtn = document.getElementById('profileSettingsBack');
  if (settingsBackBtn) {
    settingsBackBtn.addEventListener('click', () => {
      showProfileView();
      loadInfoTab();
    });
  }

  // Tab switching
  const tabs = modal.querySelectorAll('.profile-tabs .modal-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      switchProfileTab(tab.dataset.target);
    });
  });

  // Avatar editing (settings view)
  const avatarWrapper = document.getElementById('profileAvatarWrapper');
  const avatarInput = document.getElementById('profileAvatarInput');
  const editBtn = document.getElementById('profileAvatarEditBtn');
  const dropdown = document.getElementById('profileAvatarDropdown');
  const uploadBtn = document.getElementById('profileAvatarUploadBtn');
  const removeBtn = document.getElementById('profileAvatarRemoveBtn');

  function closeAvatarDropdown() {
    if (dropdown) dropdown.style.display = 'none';
  }

  if (editBtn && dropdown) {
    editBtn.addEventListener('click', () => {
      const isOpen = dropdown.style.display !== 'none';
      dropdown.style.display = isOpen ? 'none' : '';
    });
  }

  if (uploadBtn && avatarInput) {
    uploadBtn.addEventListener('click', () => {
      closeAvatarDropdown();
      avatarInput.value = '';
      avatarInput.click();
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      closeAvatarDropdown();
      deleteAvatar();
    });
  }

  if (avatarWrapper && avatarInput) {
    avatarWrapper.addEventListener('click', () => {
      avatarInput.value = '';
      avatarInput.click();
    });

    avatarWrapper.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        avatarInput.value = '';
        avatarInput.click();
      }
    });

    avatarInput.addEventListener('change', () => {
      if (avatarInput.files?.[0]) {
        handleAvatarFileSelect(avatarInput.files[0]);
      }
    });

    avatarWrapper.addEventListener('dragover', (e) => {
      e.preventDefault();
      avatarWrapper.style.borderColor = 'var(--main-accent-color)';
    });

    avatarWrapper.addEventListener('dragleave', () => {
      avatarWrapper.style.borderColor = '';
    });

    avatarWrapper.addEventListener('drop', (e) => {
      e.preventDefault();
      avatarWrapper.style.borderColor = '';
      const file = e.dataTransfer?.files?.[0];
      if (file) handleAvatarFileSelect(file);
    });
  }

  document.addEventListener('click', (e) => {
    if (dropdown && !dropdown.contains(e.target) && e.target !== editBtn) {
      closeAvatarDropdown();
    }
  });

  // Settings save (single button for name + bio)
  const nameInput = document.getElementById('profileNameInput');
  const nameError = document.getElementById('profileNameError');
  const bioInput = document.getElementById('profileBioInput');
  const bioCharcount = document.getElementById('profileBioCharcount');
  const settingsSaveBtn = document.getElementById('profileSettingsSaveBtn');
  const settingsError = document.getElementById('profileSettingsError');

  async function saveSettings() {
    if (nameError) { nameError.textContent = ''; nameError.style.display = 'none'; }
    if (settingsError) { settingsError.textContent = ''; settingsError.style.display = 'none'; }

    const newName = nameInput?.value.trim();
    const newBio = stripEmptyLines(bioInput?.value.trim() ?? '');

    if (!newName) {
      if (nameError) { nameError.textContent = 'name cannot be empty'; nameError.style.display = ''; }
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(newName)) {
      if (nameError) { nameError.textContent = 'only letters, numbers, dashes and underscores allowed'; nameError.style.display = ''; }
      return;
    }

    if (settingsSaveBtn) settingsSaveBtn.disabled = true;

    try {
      const nameChanged = newName !== getDisplayName();
      const bioChanged = newBio !== (backendUser?.bio || '');

      if (nameChanged) {
        await updateBackendDisplayName(newName);
      }

      if (bioChanged) {
        const res = await authFetch(`${apiUrl}/me`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bio: newBio }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || data.error || 'failed to update bio');
        }

        const data = await res.json();
        if (data.user) {
          backendUser = data.user;
        } else {
          backendUser = { ...backendUser, bio: newBio };
        }
      }

      showProfileView();
      loadInfoTab();
    } catch (err) {
      if (settingsError) {
        settingsError.textContent = err.message || 'failed to save changes';
        settingsError.style.display = '';
      }
    } finally {
      if (settingsSaveBtn) settingsSaveBtn.disabled = false;
    }
  }

  if (settingsSaveBtn) settingsSaveBtn.addEventListener('click', saveSettings);

  if (bioInput) {
    bioInput.addEventListener('input', () => {
      if (bioCharcount) bioCharcount.textContent = `${bioInput.value.length}/100`;
    });
  }

  // Crop modal controls
  const cropClose = document.getElementById('avatarCropClose');
  const cropCancel = document.getElementById('avatarCropCancel');
  const cropConfirm = document.getElementById('avatarCropConfirm');
  const cropModal = document.getElementById('avatarCropModal');

  if (cropClose) cropClose.addEventListener('click', closeCropModal);
  if (cropCancel) cropCancel.addEventListener('click', closeCropModal);
  if (cropConfirm) cropConfirm.addEventListener('click', cropAndUpload);

  if (cropModal) {
    cropModal.addEventListener('click', (e) => {
      if (e.target === cropModal) closeCropModal();
    });
  }

  installProfileStatsRoomNavHandlers(modal);
}

// ── Authenticated Fetch ──

export async function authFetch(url, options = {}) {
  const token = await getToken();
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

// ── User Card Modal (read-only) ──

let userCardModalEl = null;
let userCardCurrentUserId = null;
const loadedUserCardTabs = new Set();

function getUserCardModal() {
  if (!userCardModalEl) {
    userCardModalEl = document.getElementById('userCardModal');
  }
  return userCardModalEl;
}

function closeUserCard() {
  const modal = getUserCardModal();
  if (!modal) return;
  modal.classList.remove('active');
  modalClose();
}

function installProfileStatsRoomNavHandlers(modal) {
  if (!modal || modal.dataset.roomNavInstalled) return;
  modal.dataset.roomNavInstalled = '1';
  modal.addEventListener('click', (e) => {
    const btn = e.target.closest('.profile-stats-room-nav');
    if (!btn || !modal.contains(btn)) return;
    const room = btn.dataset.room;
    if (!room) return;
    e.preventDefault();
    e.stopPropagation();
    closeProfileModal();
    closeUserCard();
    router.navigate(`/${encodeURIComponent(room)}`);
  });
}

function switchUserCardTab(targetId, { force = false } = {}) {
  const modal = getUserCardModal();
  if (!modal) return;

  if (!force) {
    const currentActive = modal.querySelector('.profile-tabs .modal-tab.active');
    if (currentActive?.dataset?.target === targetId) return;
  }

  const tabs = modal.querySelectorAll('.profile-tabs .modal-tab');
  const contents = modal.querySelectorAll('.modal-content-wrapper .tab-content');

  tabs.forEach(t => t.classList.remove('active'));
  contents.forEach(c => { c.classList.remove('active'); c.style.display = 'none'; });

  const targetTab = modal.querySelector(`.profile-tabs .modal-tab[data-target="${targetId}"]`);
  const targetContent = modal.querySelector(`#${targetId}`);

  if (targetTab) targetTab.classList.add('active');
  if (targetContent) { targetContent.classList.add('active'); targetContent.style.display = ''; }

  if (loadedUserCardTabs.has(targetId)) return;
  loadedUserCardTabs.add(targetId);

  if (targetId === 'userCardLikesTab') {
    loadLikesTab(userCardCurrentUserId, 'userCardLikesGrid', 'userCardLikesLoading', 'userCardLikesEmpty');
  }
  if (targetId === 'userCardStatsTab') {
    loadStatsTab(userCardCurrentUserId, 'userCardStatsSkeleton', 'userCardStatsContent', 'userCardStatsSummary', 'userCardStatsBarChart', 'userCardStatsChart', 'userCardStatsEmpty', 'userCardStatsRecent', 'userCard');
  }
}

export function initUserCardModal() {
  const modal = getUserCardModal();
  if (!modal) return;

  const closeBtn = modal.querySelector('#userCardClose');
  if (closeBtn) closeBtn.addEventListener('click', closeUserCard);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeUserCard();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeUserCard();
    }
  });

  const tabs = modal.querySelectorAll('.profile-tabs .modal-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      switchUserCardTab(tab.dataset.target);
    });
  });

  installProfileStatsRoomNavHandlers(modal);
}

export async function openUserCard(userId) {
  const modal = getUserCardModal();
  if (!modal) return;

  clearLikesState('userCardLikes');
  userCardCurrentUserId = userId;
  loadedUserCardTabs.clear();
  clearUserStatsCache('userCard');
  clearFeedActivityCache('userCard');
  switchUserCardTab('userCardInfoTab', { force: true });

  const skeleton = document.getElementById('userCardSkeleton');
  const content = document.getElementById('userCardInfoContent');
  const error = document.getElementById('userCardError');
  const avatarImg = document.getElementById('userCardAvatarImg');
  const avatarPlaceholder = document.getElementById('userCardAvatarPlaceholder');
  const usernameEl = document.getElementById('userCardUsername');
  const bioTextEl = document.getElementById('userCardBioText');
  const bioContainer = document.getElementById('userCardBioContainer');

  if (skeleton) skeleton.style.display = '';
  if (content) content.style.display = 'none';
  if (error) { error.textContent = ''; error.style.display = 'none'; }

  modal.classList.add('active');
  modalOpen();

  try {
    const res = await fetch(`${apiUrl}/users/${userId}`);

    if (res.status === 404) {
      if (skeleton) skeleton.style.display = 'none';
      if (error) { error.textContent = 'user not found'; error.style.display = ''; }
      return;
    }

    if (!res.ok) {
      throw new Error('failed to load user');
    }

    const data = await res.json();
    const user = data.user || data;

    const deletedBanner = document.getElementById('userCardDeletedBanner');
    if (deletedBanner) {
      deletedBanner.style.display = user?.deleted ? '' : 'none';
    }

    if (avatarImg && user.avatar_url) {
      avatarImg.src = user.avatar_url;
      avatarImg.style.display = '';
      if (avatarPlaceholder) avatarPlaceholder.style.display = 'none';
    } else {
      if (avatarImg) { avatarImg.style.display = 'none'; avatarImg.src = ''; }
      if (avatarPlaceholder) avatarPlaceholder.style.display = '';
    }

    if (usernameEl) usernameEl.textContent = user.display_name || 'user';

    const bio = stripEmptyLines(user.bio || '');
    if (bio) {
      if (bioTextEl) bioTextEl.textContent = bio;
      if (bioContainer) { bioContainer.style.display = ''; bioContainer.classList.add('has-bio'); }
    } else {
      if (bioTextEl) bioTextEl.textContent = '';
      if (bioContainer) { bioContainer.style.display = 'none'; bioContainer.classList.remove('has-bio'); }
    }

    renderAdminRooms('userCardInfoAdminRooms', user.room_admin_users);

    const joinedEl = document.getElementById('userCardJoined');
    if (joinedEl && user.created_at) {
      joinedEl.textContent = formatJoinedLine(user);
    } else if (joinedEl) {
      joinedEl.textContent = '';
    }

    if (skeleton) skeleton.style.display = 'none';
    if (content) content.style.display = '';

    fetchAndRenderInlineStats(userId, 'userCardInfoStatsSummary', null, 'userCard');
  } catch (err) {
    if (skeleton) skeleton.style.display = 'none';
    if (error) { error.textContent = err.message || 'something went wrong'; error.style.display = ''; }
  }
}
